<template>
  <CollapsableSection
    v-if="showStateChanges"
    :showNewBadge="true"
    :title="getCardTitle"
  >
    <Container
      :class="$style.container"
    >
      <ItemListPagination
        :columns="tableColumns"
        :currentPageNum="currentPageNum"
        :isLoading="isLoading"
        :itemComponent="$options.ScheduledEventRow"
        :rowsPerPage="rowsPerPage"
        :tableData="getTableData"
        :totalCount="getTotalCount"
        @pageChange="handlePageChange"
      />
    </Container>
    <div
      :class="$style.menuContainer"
    >
      <DropDownMenu
        :classNames="getMenuClassNames"
        data-test-id="ScheduledEvents__changeMenu"
        displayMenuListOnTop
        :menuSections="dropDownMenuSections"
        menuTitle="Schedule new state change"
      />
    </div>
    <ActivateUserModal
      v-if="showActivateUserModal"
      data-test-id="ScheduledEvents__ActivateUserModal"
      :item="item"
      :showModal="showActivateUserModal"
      @cancel="closeActivateUserModal"
      @close="closeActivateUserModal"
      @save="closeActivateUserModal"
    />
    <SuspendUserModal
      v-if="showSuspendUserModal"
      :item="item"
      :showModal="showSuspendUserModal"
      @close="closeSuspendUserModal"
      @save="closeSuspendUserModal"
    />
  </CollapsableSection>
</template>

<script>

import {
  Container,
  ItemListPagination,
} from '@jumpcloud/ui-components';
import { mapActions, mapState } from 'vuex';
import { userStateActionType } from 'client/systemusers/constants/SystemUserConstants';
import ActivateUserModal from 'client/systemusers/Modals/ActivateUserModal.vue';
import CollapsableSection from 'components/ContentSection/CollapsableSection.vue';
import DropDownMenu from 'client/components/DropDownMenu/DropDownMenu.vue';
import EventTableColumns from 'client/systemusers/UserDetails/ScheduledEvents/EventTableColumns';
import ScheduledEventRow from 'client/systemusers/UserDetails/ScheduledEvents/ScheduledEventRow.vue';
import SuspendUserModal from 'client/systemusers/Modals/SuspendUserModal.vue';

const systemUserCollection = 'SystemUserCollection';
const systemUserModel = 'SystemUserModel';

export default {
  name: 'ScheduledEvents',

  components: {
    ActivateUserModal,
    CollapsableSection,
    Container,
    DropDownMenu,
    ItemListPagination,
    SuspendUserModal,
  },

  data: () => ({
    currentPageNum: 0,
    isLoading: false,
    rowsPerPage: 10,
    showActivateUserModal: false,
    showSuspendUserModal: false,
    stateChangeAction: '',
  }),

  ScheduledEventRow,

  computed: {
    ...mapState(systemUserCollection, {
      modifiedItemCount: state => state.features.modifiedItemCount,
    }),

    ...mapState(systemUserModel, ['item', 'scheduledStateChangesForUser']),

    dropDownMenuSections() {
      const menuItems = [];

      menuItems.push({
        label: 'Activate',
        action: () => this.scheduleStateChange(userStateActionType.activate),
      });

      menuItems.push({
        label: 'Suspend',
        action: () => this.scheduleStateChange(userStateActionType.suspend),
      });

      return [{ menuItems }];
    },

    getCardTitle() {
      return `Scheduled User State Changes (${this.getTotalCount})`;
    },

    getMenuClassNames() {
      return {
        dropDownList: 'changeStateDropDownList',
        dropDownMenuTitle: 'changeStateMenuTitle',
      };
    },

    getTableData() {
      const start = this.currentPageNum * this.rowsPerPage;
      const end = start + this.rowsPerPage;

      return this.scheduledStateChangesForUser.slice(start, end);
    },

    getTotalCount() {
      return this.scheduledStateChangesForUser.length;
    },

    showStateChanges() {
      return this.getTotalCount > 0;
    },

    tableColumns() {
      return EventTableColumns;
    },
  },

  watch: {
    /**
    * The `modifiedItemCount` is updated when a user state change is scheduled. When the count
    * changes, fetch the scheduled changes so that the display is updated if an earlier job is
    * scheduled.
    */
    async modifiedItemCount() {
      await this.updateScheduledChanges();
    },
  },

  mounted() {
    this.updateScheduledChanges();
  },

  methods: {
    ...mapActions(systemUserModel, ['fetchScheduledStateChanges']),

    closeActivateUserModal() {
      this.showActivateUserModal = false;
    },

    closeSuspendUserModal() {
      this.showSuspendUserModal = false;
    },

    async handlePageChange(payload = {}) {
      if (payload) {
        const {
          currentPageNum = this.currentPageNum,
          rowsPerPage = this.rowsPerPage,
        } = payload;

        this.isLoading = true;

        this.currentPageNum = currentPageNum;
        this.rowsPerPage = rowsPerPage;

        this.isLoading = false;
      }
    },

    scheduleStateChange(type) {
      this.stateChangeAction = type;

      if (type === userStateActionType.suspend) {
        this.showSuspendUserModal = true;
      } else {
        this.showActivateUserModal = true;
      }
    },

    async updateScheduledChanges() {
      await this.fetchScheduledStateChanges();
    },
  },
};
</script>

<style module scoped>
.container.container {
  --container-padding: 0;

  margin-left: var(--jc-spacer-medium);
}

.menuContainer {
  display: flex;
  margin: 30px 0 0 28px;
}
</style>
